/**
 * EchoPaste IndexedDB Storage
 *
 * Store 1 ("profile"): the local development sync profile (roomId,
 * deviceId, deviceName, and non-extractable CryptoKey).
 *
 * Store 2 ("pending_queue", added in Phase 2D): Windows native offline
 * queue of ENCRYPTED clip envelopes awaiting upload when the server/network
 * is unavailable. See ../queue/pendingQueue.ts for the read/write API.
 *
 * NOTE: Plaintext clipboard data is NEVER persisted to IndexedDB or
 * localStorage, in either store. Only room identity, cryptographic key
 * material, and already-encrypted ClipEnvelope ciphertext are stored
 * locally.
 */

import type { ClipEnvelope } from "@echopaste/protocol";
import {
  base64UrlToUint8Array,
  generateLocalRoomWrappingKey,
  uint8ArrayToBase64Url,
  unwrapLocalRoomKey,
  wrapLocalRoomKey,
} from "@echopaste/crypto";

const DB_NAME = "echopaste_profile_db";
const DB_VERSION = 5;
const STORE_NAME = "profile";
const PROFILE_KEY = "current";

// Phase 2D: pending encrypted queue object store. Kept separate from the
// profile store so profile reads/writes never need to touch queue rows and
// vice versa.
export const PENDING_QUEUE_STORE = "pending_queue";
const PRODUCTION_IDENTITY_STORE = "production_device_identity";
const PRODUCTION_IDENTITY_KEY = "current";
const PRODUCTION_ROOM_STORE = "production_room_keys";
export const PRODUCTION_ROOM_KEY_VERSIONS_STORE = "production_room_key_versions";

export interface StoredProductionDeviceIdentity {
  id: "current";
  deviceId: string;
  signingPrivateKey: CryptoKey;
  signingPublicKeySpki: string;
  signingFingerprint: string;
  agreementPrivateKey: CryptoKey;
  agreementPublicKeySpki: string;
  agreementFingerprint: string;
  createdAt: string;
}

export interface StoredSyncProfile {
  id: "current";
  roomId: string;
  deviceId: string;
  deviceName: string;
  key: CryptoKey;
  createdAt: string;
}

/** Separate from EP1 profiles. Web Crypto / IndexedDB protection is browser best-effort, not native secure storage. */
export interface StoredProductionRoom {
  roomId: string; keyVersion: number; localWrappingKey: CryptoKey;
  wrappedRoomKeyCiphertext: string; wrappedRoomKeyNonce: string;
  state: "pending" | "active"; createdAt: string; updatedAt: string;
}

/** Phase 4 historical room key version store. Raw keys are NEVER stored; ciphertext/nonce in binary. */
export interface StoredVersionedRoomKey {
  roomId: string;
  keyVersion: number;
  localWrappingKey: CryptoKey;
  wrappedRoomKeyCiphertext: ArrayBuffer;
  wrappedRoomKeyNonce: Uint8Array;
  createdAt: string;
}

export interface VersionedRoomKeyInput {
  roomId: string;
  keyVersion: number;
  localWrappingKey: CryptoKey;
  wrappedRoomKeyCiphertext: ArrayBuffer | string;
  wrappedRoomKeyNonce: Uint8Array | string;
  createdAt?: string;
}

function toArrayBuffer(input: ArrayBuffer | Uint8Array | string): ArrayBuffer {
  if (typeof input === "string") {
    const u8 = base64UrlToUint8Array(input);
    return u8.buffer.slice(u8.byteOffset, u8.byteOffset + u8.byteLength) as ArrayBuffer;
  }
  if (input instanceof Uint8Array) {
    return input.buffer.slice(input.byteOffset, input.byteOffset + input.byteLength) as ArrayBuffer;
  }
  return input;
}

function toUint8Array(input: ArrayBuffer | Uint8Array | string): Uint8Array {
  if (typeof input === "string") {
    return base64UrlToUint8Array(input);
  }
  if (input instanceof Uint8Array) {
    return input;
  }
  return new Uint8Array(input);
}

function toBase64Url(input: ArrayBuffer | Uint8Array | string): string {
  if (typeof input === "string") {
    return input;
  }
  if (input instanceof Uint8Array) {
    return uint8ArrayToBase64Url(input);
  }
  return uint8ArrayToBase64Url(new Uint8Array(input));
}

/**
 * A single Windows-native offline queue entry. Only ever holds an already
 * client-side-encrypted ClipEnvelope (ciphertext + nonce) — never plaintext,
 * never the pairing token/key material.
 */
export interface PendingQueueEntry {
  /** Same id as the underlying ClipEnvelope.id -- used as the IndexedDB key
   * so retries reuse the identical logical clip id (no re-encrypt / no new
   * clip id on retry). */
  id: string;
  roomId: string;
  originDeviceId: string;
  envelope: ClipEnvelope;
  queuedAt: string;
  /** Number of upload attempts made so far. Informational only; Phase 2D
   * does not implement a backoff engine beyond the existing WebSocket
   * reconnect backoff. */
  attempts: number;
  lastAttemptAt?: string;
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      return reject(new Error("IndexedDB is not available in this environment"));
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(PENDING_QUEUE_STORE)) {
        const queueStore = db.createObjectStore(PENDING_QUEUE_STORE, { keyPath: "id" });
        queueStore.createIndex("roomId", "roomId", { unique: false });
      }
      if (!db.objectStoreNames.contains(PRODUCTION_IDENTITY_STORE)) {
        db.createObjectStore(PRODUCTION_IDENTITY_STORE, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(PRODUCTION_ROOM_STORE)) {
        db.createObjectStore(PRODUCTION_ROOM_STORE, { keyPath: "roomId" });
      }
      if (!db.objectStoreNames.contains(PRODUCTION_ROOM_KEY_VERSIONS_STORE)) {
        const versionsStore = db.createObjectStore(PRODUCTION_ROOM_KEY_VERSIONS_STORE, {
          keyPath: ["roomId", "keyVersion"],
        });
        versionsStore.createIndex("roomId", "roomId", { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/** Pre-rotation migration: before storing a new key version, ensure the old version in production_room_keys is also saved into production_room_key_versions. */
export async function ensureProductionRoomKeyMigrated(roomId: string): Promise<void> {
  const current = await loadProductionRoom(roomId);
  if (!current) return;
  const db = await openDB();
  const alreadyInVersions = await new Promise<boolean>((resolve, reject) => {
    const tx = db.transaction(PRODUCTION_ROOM_KEY_VERSIONS_STORE, "readonly");
    const store = tx.objectStore(PRODUCTION_ROOM_KEY_VERSIONS_STORE);
    const req = store.get([roomId, current.keyVersion]);
    req.onsuccess = () => resolve(Boolean(req.result));
    req.onerror = () => reject(req.error);
  });
  if (!alreadyInVersions) {
    const record: StoredVersionedRoomKey = {
      roomId: current.roomId,
      keyVersion: current.keyVersion,
      localWrappingKey: current.localWrappingKey,
      wrappedRoomKeyCiphertext: toArrayBuffer(current.wrappedRoomKeyCiphertext),
      wrappedRoomKeyNonce: toUint8Array(current.wrappedRoomKeyNonce),
      createdAt: current.createdAt,
    };
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(PRODUCTION_ROOM_KEY_VERSIONS_STORE, "readwrite");
      const store = tx.objectStore(PRODUCTION_ROOM_KEY_VERSIONS_STORE);
      const req = store.put(record);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }
}

export async function saveVersionedRoomKey(input: VersionedRoomKeyInput): Promise<StoredVersionedRoomKey> {
  await ensureProductionRoomKeyMigrated(input.roomId);
  const db = await openDB();
  const existing = await getVersionedRoomKey(input.roomId, input.keyVersion);
  if (existing) {
    throw new Error(`Cannot overwrite historical room key version ${input.keyVersion} for room ${input.roomId}`);
  }
  const record: StoredVersionedRoomKey = {
    roomId: input.roomId,
    keyVersion: input.keyVersion,
    localWrappingKey: input.localWrappingKey,
    wrappedRoomKeyCiphertext: toArrayBuffer(input.wrappedRoomKeyCiphertext),
    wrappedRoomKeyNonce: toUint8Array(input.wrappedRoomKeyNonce),
    createdAt: input.createdAt ?? new Date().toISOString(),
  };
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(PRODUCTION_ROOM_KEY_VERSIONS_STORE, "readwrite");
    const store = tx.objectStore(PRODUCTION_ROOM_KEY_VERSIONS_STORE);
    const req = store.put(record);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
  return record;
}

export async function getVersionedRoomKey(roomId: string, keyVersion: number): Promise<StoredVersionedRoomKey | null> {
  try {
    const db = await openDB();
    const result = await new Promise<StoredVersionedRoomKey | null>((resolve, reject) => {
      const tx = db.transaction(PRODUCTION_ROOM_KEY_VERSIONS_STORE, "readonly");
      const store = tx.objectStore(PRODUCTION_ROOM_KEY_VERSIONS_STORE);
      const req = store.get([roomId, keyVersion]);
      req.onsuccess = () => resolve((req.result as StoredVersionedRoomKey) || null);
      req.onerror = () => reject(req.error);
    });
    if (result) {
      return {
        ...result,
        wrappedRoomKeyCiphertext: toArrayBuffer(result.wrappedRoomKeyCiphertext),
        wrappedRoomKeyNonce: toUint8Array(result.wrappedRoomKeyNonce),
      };
    }
    const current = await loadProductionRoom(roomId);
    if (current && current.keyVersion === keyVersion) {
      await ensureProductionRoomKeyMigrated(roomId);
      return {
        roomId: current.roomId,
        keyVersion: current.keyVersion,
        localWrappingKey: current.localWrappingKey,
        wrappedRoomKeyCiphertext: toArrayBuffer(current.wrappedRoomKeyCiphertext),
        wrappedRoomKeyNonce: toUint8Array(current.wrappedRoomKeyNonce),
        createdAt: current.createdAt,
      };
    }
    return null;
  } catch {
    return null;
  }
}

export async function listVersionedRoomKeys(roomId: string): Promise<StoredVersionedRoomKey[]> {
  try {
    await ensureProductionRoomKeyMigrated(roomId);
    const db = await openDB();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(PRODUCTION_ROOM_KEY_VERSIONS_STORE, "readonly");
      const store = tx.objectStore(PRODUCTION_ROOM_KEY_VERSIONS_STORE);
      const index = store.index("roomId");
      const req = index.getAll(IDBKeyRange.only(roomId));
      req.onsuccess = () => {
        const items = (req.result as StoredVersionedRoomKey[]) || [];
        const normalized = items.map((item) => ({
          ...item,
          wrappedRoomKeyCiphertext: toArrayBuffer(item.wrappedRoomKeyCiphertext),
          wrappedRoomKeyNonce: toUint8Array(item.wrappedRoomKeyNonce),
        }));
        normalized.sort((a, b) => a.keyVersion - b.keyVersion);
        resolve(normalized);
      };
      req.onerror = () => reject(req.error);
    });
  } catch {
    return [];
  }
}

export async function deleteVersionedRoomKey(roomId: string, keyVersion: number): Promise<void> {
  const db = await openDB();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(PRODUCTION_ROOM_KEY_VERSIONS_STORE, "readwrite");
    const store = tx.objectStore(PRODUCTION_ROOM_KEY_VERSIONS_STORE);
    const req = store.delete([roomId, keyVersion]);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function promoteCurrentRoomKey(input: VersionedRoomKeyInput): Promise<StoredVersionedRoomKey> {
  await ensureProductionRoomKeyMigrated(input.roomId);
  const db = await openDB();
  const current = await loadProductionRoom(input.roomId);

  const record: StoredVersionedRoomKey = {
    roomId: input.roomId,
    keyVersion: input.keyVersion,
    localWrappingKey: input.localWrappingKey,
    wrappedRoomKeyCiphertext: toArrayBuffer(input.wrappedRoomKeyCiphertext),
    wrappedRoomKeyNonce: toUint8Array(input.wrappedRoomKeyNonce),
    createdAt: input.createdAt ?? new Date().toISOString(),
  };

  const existingVersion = await getVersionedRoomKey(input.roomId, input.keyVersion);
  if (!existingVersion) {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(PRODUCTION_ROOM_KEY_VERSIONS_STORE, "readwrite");
      const store = tx.objectStore(PRODUCTION_ROOM_KEY_VERSIONS_STORE);
      const req = store.put(record);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  const now = new Date().toISOString();
  const updatedCurrent: StoredProductionRoom = {
    roomId: input.roomId,
    keyVersion: input.keyVersion,
    localWrappingKey: input.localWrappingKey,
    wrappedRoomKeyCiphertext: toBase64Url(input.wrappedRoomKeyCiphertext),
    wrappedRoomKeyNonce: toBase64Url(input.wrappedRoomKeyNonce),
    state: current?.state ?? "active",
    createdAt: current?.createdAt ?? now,
    updatedAt: now,
  };

  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(PRODUCTION_ROOM_STORE, "readwrite");
    const store = tx.objectStore(PRODUCTION_ROOM_STORE);
    const req = store.put(updatedCurrent);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });

  return record;
}

export async function unwrapVersionedRoomKey(record: StoredVersionedRoomKey | VersionedRoomKeyInput): Promise<string> {
  const ciphertext = toBase64Url(record.wrappedRoomKeyCiphertext);
  const nonce = toBase64Url(record.wrappedRoomKeyNonce);
  return unwrapLocalRoomKey({ ciphertext, nonce }, record.localWrappingKey);
}

/** Atomically installs a recovered keyring. Existing local production material is never replaced. */
export async function restorePendingProductionRoomKeyring(input: {
  roomId: string;
  currentKeyVersion: number;
  versions: VersionedRoomKeyInput[];
  restoredAt: string;
}): Promise<void> {
  const currentVersion = input.versions.find((version) => version.keyVersion === input.currentKeyVersion);
  if (!currentVersion || input.versions.length === 0 || new Set(input.versions.map((version) => version.keyVersion)).size !== input.versions.length) {
    throw new Error("Invalid recovered room keyring");
  }
  const db = await openDB();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction([PRODUCTION_ROOM_STORE, PRODUCTION_ROOM_KEY_VERSIONS_STORE], "readwrite");
    const roomStore = tx.objectStore(PRODUCTION_ROOM_STORE);
    const versionsStore = tx.objectStore(PRODUCTION_ROOM_KEY_VERSIONS_STORE);
    let settled = false;
    const fail = (error: unknown) => {
      if (!settled) { settled = true; reject(error); }
    };
    const currentRequest = roomStore.get(input.roomId);
    const versionKeysRequest = versionsStore.index("roomId").getAllKeys(IDBKeyRange.only(input.roomId));
    let currentRead = false;
    let versionsRead = false;
    let existingCurrent: StoredProductionRoom | undefined;
    let existingVersionKeys: IDBValidKey[] = [];
    const writeIfEmpty = () => {
      if (!currentRead || !versionsRead) return;
      if (existingCurrent || existingVersionKeys.length > 0) {
        tx.abort();
        fail(new Error("Production room key material already exists"));
        return;
      }
      for (const version of input.versions) {
        versionsStore.put({
          roomId: input.roomId,
          keyVersion: version.keyVersion,
          localWrappingKey: version.localWrappingKey,
          wrappedRoomKeyCiphertext: toArrayBuffer(version.wrappedRoomKeyCiphertext),
          wrappedRoomKeyNonce: toUint8Array(version.wrappedRoomKeyNonce),
          createdAt: version.createdAt ?? input.restoredAt,
        } satisfies StoredVersionedRoomKey);
      }
      roomStore.put({
        roomId: input.roomId,
        keyVersion: input.currentKeyVersion,
        localWrappingKey: currentVersion.localWrappingKey,
        wrappedRoomKeyCiphertext: toBase64Url(currentVersion.wrappedRoomKeyCiphertext),
        wrappedRoomKeyNonce: toBase64Url(currentVersion.wrappedRoomKeyNonce),
        state: "pending",
        createdAt: input.restoredAt,
        updatedAt: input.restoredAt,
      } satisfies StoredProductionRoom);
    };
    currentRequest.onsuccess = () => { currentRead = true; existingCurrent = currentRequest.result as StoredProductionRoom | undefined; writeIfEmpty(); };
    versionKeysRequest.onsuccess = () => { versionsRead = true; existingVersionKeys = versionKeysRequest.result; writeIfEmpty(); };
    currentRequest.onerror = () => fail(currentRequest.error);
    versionKeysRequest.onerror = () => fail(versionKeysRequest.error);
    tx.oncomplete = () => { if (!settled) { settled = true; resolve(); } };
    tx.onerror = () => fail(tx.error);
    tx.onabort = () => fail(tx.error ?? new Error("Recovered room keyring transaction aborted"));
  });
}

export async function savePendingProductionRoom(input: { roomId: string; keyVersion: number; rawRoomKeyMaterial: string }): Promise<void> {
  const localWrappingKey = await generateLocalRoomWrappingKey();
  const wrapped = await wrapLocalRoomKey(input.rawRoomKeyMaterial, localWrappingKey);
  const now = new Date().toISOString();
  const record: StoredProductionRoom = { roomId: input.roomId, keyVersion: input.keyVersion, localWrappingKey, wrappedRoomKeyCiphertext: wrapped.ciphertext, wrappedRoomKeyNonce: wrapped.nonce, state: "pending", createdAt: now, updatedAt: now };
  const db = await openDB();
  await new Promise<void>((resolve, reject) => { const req = db.transaction(PRODUCTION_ROOM_STORE, "readwrite").objectStore(PRODUCTION_ROOM_STORE).put(record); req.onsuccess = () => resolve(); req.onerror = () => reject(req.error); });
  const versioned: StoredVersionedRoomKey = {
    roomId: input.roomId,
    keyVersion: input.keyVersion,
    localWrappingKey,
    wrappedRoomKeyCiphertext: toArrayBuffer(wrapped.ciphertext),
    wrappedRoomKeyNonce: toUint8Array(wrapped.nonce),
    createdAt: now,
  };
  await new Promise<void>((resolve, reject) => {
    const req = db.transaction(PRODUCTION_ROOM_KEY_VERSIONS_STORE, "readwrite").objectStore(PRODUCTION_ROOM_KEY_VERSIONS_STORE).put(versioned);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function loadProductionRoom(roomId: string): Promise<StoredProductionRoom | null> {
  try { const db = await openDB(); return await new Promise((resolve, reject) => { const req = db.transaction(PRODUCTION_ROOM_STORE, "readonly").objectStore(PRODUCTION_ROOM_STORE).get(roomId); req.onsuccess = () => resolve((req.result as StoredProductionRoom) || null); req.onerror = () => reject(req.error); }); } catch { return null; }
}
export async function unwrapProductionRoomKey(record: StoredProductionRoom): Promise<string> { return unwrapLocalRoomKey({ ciphertext: record.wrappedRoomKeyCiphertext, nonce: record.wrappedRoomKeyNonce }, record.localWrappingKey); }
export async function activateProductionRoom(roomId: string): Promise<void> { const room = await loadProductionRoom(roomId); if (!room) throw new Error("Production room not found"); const db = await openDB(); await new Promise<void>((resolve, reject) => { const req = db.transaction(PRODUCTION_ROOM_STORE, "readwrite").objectStore(PRODUCTION_ROOM_STORE).put({ ...room, state: "active", updatedAt: new Date().toISOString() }); req.onsuccess = () => resolve(); req.onerror = () => reject(req.error); }); }
export async function clearProductionRoom(roomId: string): Promise<void> {
  const db = await openDB();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction([PRODUCTION_ROOM_STORE, PRODUCTION_ROOM_KEY_VERSIONS_STORE], "readwrite");
    tx.objectStore(PRODUCTION_ROOM_STORE).delete(roomId);
    const versionsStore = tx.objectStore(PRODUCTION_ROOM_KEY_VERSIONS_STORE);
    const index = versionsStore.index("roomId");
    const req = index.getAllKeys(IDBKeyRange.only(roomId));
    req.onsuccess = () => {
      const keys = req.result;
      for (const key of keys) {
        versionsStore.delete(key);
      }
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

/** Phase 4B production identity is deliberately isolated from Development Sync Room profiles. */
export async function loadProductionDeviceIdentity(): Promise<StoredProductionDeviceIdentity | null> {
  try {
    const db = await openDB();
    return await new Promise((resolve, reject) => {
      const request = db.transaction(PRODUCTION_IDENTITY_STORE, "readonly").objectStore(PRODUCTION_IDENTITY_STORE).get(PRODUCTION_IDENTITY_KEY);
      request.onsuccess = () => resolve((request.result as StoredProductionDeviceIdentity) || null);
      request.onerror = () => reject(request.error);
    });
  } catch { return null; }
}

export async function saveProductionDeviceIdentity(identity: Omit<StoredProductionDeviceIdentity, "id">): Promise<void> {
  const db = await openDB();
  await new Promise<void>((resolve, reject) => {
    const request = db.transaction(PRODUCTION_IDENTITY_STORE, "readwrite").objectStore(PRODUCTION_IDENTITY_STORE).put({ ...identity, id: PRODUCTION_IDENTITY_KEY });
    request.onsuccess = () => resolve(); request.onerror = () => reject(request.error);
  });
}

export async function clearProductionDeviceIdentity(): Promise<void> {
  try {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const request = db.transaction(PRODUCTION_IDENTITY_STORE, "readwrite").objectStore(PRODUCTION_IDENTITY_STORE).delete(PRODUCTION_IDENTITY_KEY);
      request.onsuccess = () => resolve(); request.onerror = () => reject(request.error);
    });
  } catch { /* already absent/unavailable */ }
}

export async function loadStoredProfile(): Promise<StoredSyncProfile | null> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(PROFILE_KEY);

      req.onsuccess = () => {
        resolve((req.result as StoredSyncProfile) || null);
      };
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

export async function saveStoredProfile(
  profile: Omit<StoredSyncProfile, "id">
): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const item: StoredSyncProfile = {
      ...profile,
      id: PROFILE_KEY,
    };
    const req = store.put(item);

    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function clearStoredProfile(): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const req = store.delete(PROFILE_KEY);

      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch {
    // Ignore error if already cleared or DB unopenable
  }
}

// ---------------------------------------------------------------------------
// Phase 2D: pending encrypted queue store access.
// Kept in this file (rather than a second IndexedDB database) so the schema
// version bump and store creation happen in exactly one onupgradeneeded.
// ---------------------------------------------------------------------------

export async function addPendingQueueEntry(entry: PendingQueueEntry): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(PENDING_QUEUE_STORE, "readwrite");
    const store = tx.objectStore(PENDING_QUEUE_STORE);
    const req = store.put(entry);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function updatePendingQueueEntry(entry: PendingQueueEntry): Promise<void> {
  return addPendingQueueEntry(entry);
}

export async function removePendingQueueEntry(id: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(PENDING_QUEUE_STORE, "readwrite");
    const store = tx.objectStore(PENDING_QUEUE_STORE);
    const req = store.delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function listPendingQueueEntries(): Promise<PendingQueueEntry[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(PENDING_QUEUE_STORE, "readonly");
    const store = tx.objectStore(PENDING_QUEUE_STORE);
    const req = store.getAll();
    req.onsuccess = () => resolve((req.result as PendingQueueEntry[]) || []);
    req.onerror = () => reject(req.error);
  });
}

/** Deletes every pending queue entry belonging to a given room. Called when
 * the user leaves a Development Sync Room, so a stale queue can never leak
 * an old room's clips into whatever room is joined next. */
export async function clearPendingQueueForRoom(roomId: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(PENDING_QUEUE_STORE, "readwrite");
    const store = tx.objectStore(PENDING_QUEUE_STORE);
    const index = store.index("roomId");
    const req = index.openCursor(IDBKeyRange.only(roomId));
    req.onsuccess = () => {
      const cursor = req.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      } else {
        resolve();
      }
    };
    req.onerror = () => reject(req.error);
  });
}
